import React, { useEffect, useState } from "react";

import { faTimes } from "@fortawesome/free-solid-svg-icons";
import {
  Button,
  CheckForm,
  Divider,
  Grid,
  Group,
  Search,
  Select,
  ToggleButton,
} from "@samskip/frontend-components";
import { useTranslation } from "react-i18next";

import { ShipmentType } from "@assets/constants";
import { useAppDispatch, useAppSelector } from "@assets/hooks";
import { BookingTemplateTypes } from "@assets/types";
import { classNames } from "@assets/utilities/classNameUtilities";
import {
  partnerAddressValueMapper,
  partnerLabelMapper,
} from "@assets/utilities/labelMapperUtils";
import {
  filterBookingTemplateSearch,
  filterBookingTemplatesShippingType,
  filterBookingTemplatesShipper,
  bookingTemplateFiltersState,
  activeBookingTemplateFilters,
  filterBookingTemplatesReset,
  filterBookingTemplatesConsignee,
  getTemplatePartnersFilterValues,
  bookingTemplatesState,
} from "@state/slices/bookingTemplateSlice";
import { userProfileState } from "@state/slices/userProfileSlice";

const BookingTemplateListFilter = () => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { t } = useTranslation();
  const filter = useAppSelector(bookingTemplateFiltersState);
  const activeFilters = useAppSelector(activeBookingTemplateFilters);
  const { templateCustomer } = useAppSelector(bookingTemplateFiltersState);
  const { userProfile } = useAppSelector(userProfileState);
  const shippers = useAppSelector(bookingTemplatesState).filterShippers;
  const consignees = useAppSelector(bookingTemplatesState).filterConsignees;
  const dispatch = useAppDispatch();

  const toggleFilterOpen = () => {
    setIsFilterOpen((oldIsFilterOpen) => !oldIsFilterOpen);
  };

  const handleSearchChange = (searchString: string) => {
    dispatch(filterBookingTemplateSearch(searchString));
  };

  const handleShippingTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const toggledType = e.target.value;
    let updatedShipments: string[];
    if (e.target.checked) {
      updatedShipments = [...filter.shippingTypes, toggledType];
    } else {
      updatedShipments = filter.shippingTypes.filter(
        (shType) => shType != toggledType
      );
    }
    dispatch(filterBookingTemplatesShippingType(updatedShipments));
  };

  const handleShipperChange = (
    shipperOption: BookingTemplateTypes.Partner | null
  ) => {
    dispatch(filterBookingTemplatesShipper(shipperOption?.PartnerCode ?? ""));
  };

  const handleConsigneeChange = (
    consigneeOption: BookingTemplateTypes.Partner | null
  ) => {
    dispatch(
      filterBookingTemplatesConsignee(consigneeOption?.PartnerCode ?? "")
    );
  };

  const handleResetFilters = () => {
    dispatch(filterBookingTemplatesReset());
  };

  useEffect(() => {
    dispatch(getTemplatePartnersFilterValues());
    dispatch(filterBookingTemplatesReset());
  }, [templateCustomer, userProfile?.SelectedCompany.PartnerCode]);

  return (
    <>
      <div className="flex flex-col md:flex-row items-baseline w-full gap-6">
        <Search
          className="w-full md:w-[347px]"
          id="search-template"
          onChange={(searchString) => handleSearchChange(searchString)}
          placeholder={t("PLACEHOLDER_SEARCH_TEMPLATE")}
          value={filter.searchString}
        />
        <Divider isVertical space="none" />
        <ToggleButton
          message={`${t("LABEL_FILTERS")} ${
            activeFilters.length > 0 ? `(${activeFilters.length})` : ""
          } `}
          onClick={toggleFilterOpen}
        />
      </div>
      <div
        className={classNames(
          "flex flex-col p-6 bg-neutral-100",
          !isFilterOpen && "hidden"
        )}
      >
        <Button
          className="self-end"
          disabled={activeFilters.length === 0}
          iconLeft={faTimes}
          onClick={handleResetFilters}
          variant="text"
        >
          {t("LABEL_CLEAR_FILTERS")}
        </Button>

        <Grid mdCol={3}>
          <Group title={t("LABEL_SHIPMENT_TYPE")} vertical>
            <CheckForm
              checked={filter.shippingTypes?.includes(ShipmentType.LCL)}
              id={ShipmentType.LCL}
              label={t("LABEL_LCLDEFINITION")}
              onChange={handleShippingTypeChange}
              value={ShipmentType.LCL}
            />
            <CheckForm
              checked={filter.shippingTypes?.includes(ShipmentType.FCL)}
              id={ShipmentType.FCL}
              label={t("LABEL_FCLDEFINITION")}
              onChange={handleShippingTypeChange}
              value={ShipmentType.FCL}
            />
          </Group>
          <Select
            fullWidth
            getOptionLabel={partnerLabelMapper}
            getOptionValue={partnerAddressValueMapper}
            id="shipper"
            isClearable
            label={t("LABEL_SHIPPER")}
            onChange={handleShipperChange}
            options={shippers}
            placeholder={t("PLACEHOLDER_SELECT_SHIPPER")}
            value={
              shippers.find(
                (partner) => partner.PartnerCode === filter.shipperCode
              ) ?? null
            }
          />
          <Select
            fullWidth
            getOptionLabel={partnerLabelMapper}
            getOptionValue={partnerAddressValueMapper}
            id="consignee"
            isClearable
            label={t("LABEL_CONSIGNEE")}
            onChange={handleConsigneeChange}
            options={consignees}
            placeholder={t("PLACEHOLDER_SELECT_CONSIGNEE")}
            value={
              consignees.find(
                (partner) => partner.PartnerCode === filter.consigneeCode
              ) ?? null
            }
          />
        </Grid>
      </div>
    </>
  );
};

export default BookingTemplateListFilter;
